import { Component, Input, inject, OnInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { ChatbotService } from "@bitwarden/web-vault/app/services/chatbot/chatbot.service";
import { GlossRoutes, NavItems } from "../../../models/enum/routes.enum";

const enum MessageType {
  ROUTE = "GLOSS_ROUTE",
  TRANSACTION = "GLOSS_TRANSACTION",
}

interface IFrameMessageData {
  type: string;
}

interface IFrameRoutingMessageData extends IFrameMessageData {
  type: MessageType.ROUTE;
  route: string;
}

function isRoutingMessageEvent(
  event: MessageEvent<unknown>,
): event is MessageEvent<IFrameRoutingMessageData> {
  return (
    typeof event.data === "object" &&
    event.data !== null &&
    (event.data as IFrameMessageData).type === MessageType.ROUTE
  );
}

const NAV_DESCRIPTIONS: Partial<Record<GlossRoutes, { displayName: string; pageContent: string }>> =
  {
    [GlossRoutes.Views]: {
      displayName: "Home / Transactions",
      pageContent:
        "Home dashboard showing all transactions, balances, and financial overview. Browse spending history, recent payments, income, and portfolio summary across all accounts.",
    },
    [GlossRoutes.Account]: {
      displayName: "Accounts",
      pageContent:
        "Manage linked bank accounts, investment portfolios, and financial institutions. Add, edit, or remove accounts. View account balances and sync status.",
    },
    [GlossRoutes.Budget]: {
      displayName: "Budgets",
      pageContent:
        "Budget planning and spending limits. Set monthly budgets by category, track progress against targets, and identify overspending.",
    },
    [GlossRoutes.Credits]: {
      displayName: "Credits & Rewards",
      pageContent:
        "Gloss Credits and rewards. View credit balance, earn rewards by completing tasks, redeem credits, and see rewards history.",
    },
    [GlossRoutes.Categories]: {
      displayName: "Categories",
      pageContent:
        "Manage transaction categories used to classify spending and income. Create, rename, or delete categories.",
    },
    [GlossRoutes.Classifications]: {
      displayName: "Classifications",
      pageContent:
        "Manage classifications for grouping accounts and transactions into broader segments or asset classes.",
    },
    [GlossRoutes.Institutions]: {
      displayName: "Institutions",
      pageContent:
        "Manage financial institutions and bank connections. Add or remove bank feeds and data sources.",
    },
    [GlossRoutes.Import]: {
      displayName: "Import Data",
      pageContent:
        "Import transaction data from CSV or other file formats. Upload bank statements or exported data files.",
    },
    [GlossRoutes.Reference]: {
      displayName: "Reference Data",
      pageContent:
        "Manage reference data such as currency symbols, exchange rates, and financial instrument definitions.",
    },
    [GlossRoutes.Symbol]: {
      displayName: "Symbols",
      pageContent:
        "Manage currency and asset symbols used across the app for pricing and conversion.",
    },
    [GlossRoutes.Estimates]: {
      displayName: "Estimates",
      pageContent:
        "Financial estimates and projections. View forecasts of future balances and performance based on current data.",
    },
    [GlossRoutes.SourceExplorer]: {
      displayName: "Source Explorer",
      pageContent:
        "Inspect and explore imported source transactions before they are processed. Review raw data from bank feeds and file imports.",
    },
    [GlossRoutes.Earn]: {
      displayName: "Earn",
      pageContent:
        "Earn rewards by completing tasks, watching ads, and engaging with Gloss features.",
    },
    [GlossRoutes.Plugins]: {
      displayName: "Plugins",
      pageContent:
        "Plugins and integrations marketplace. Discover, install, and manage third-party plugins that extend app functionality.",
    },
    [GlossRoutes.Cabinet]: {
      displayName: "Cabinet",
      pageContent: "Cabinet for storing and organising important financial documents and files.",
    },
    [GlossRoutes.Settings]: {
      displayName: "Settings",
      pageContent:
        "App settings including profile, security, password change, login devices, subscription plan, and advanced features.",
    },
    [GlossRoutes.Guide]: {
      displayName: "Help Guide",
      pageContent:
        "Help guide and tutorials explaining how to use Gloss. Getting started documentation and feature walkthroughs.",
    },
  };

const ROUTES_DOCUMENTS = [
  ...NavItems.filter((item) => NAV_DESCRIPTIONS[item.key as GlossRoutes] != null).map((item) => {
    const parts = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
    const route = parts.join("/").replace(/\/{2,}/g, "/");
    const desc = NAV_DESCRIPTIONS[item.key as GlossRoutes]!;
    return {
      pageContent: desc.pageContent,
      metadata: { type: "navigation", route, displayName: desc.displayName },
    };
  }),
  // Preferences is not in NavItems but is a navigable route
  {
    pageContent:
      "Manage display preferences such as default currency, date format, language, and notification settings.",
    metadata: { type: "navigation", route: "/manage/preferences", displayName: "Preferences" },
  },
];

@Component({
  selector: "app-chatbot",
  templateUrl: "./chatbot.component.html",
})
export class ChatbotComponent implements OnInit, OnDestroy {
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private readonly chatUrl: string = "https://ai.plugin.gloss-vault.com";

  @Input() title: string = "Support Chat";

  @ViewChild("chatbotIframe") chatbotIframe!: ElementRef<HTMLIFrameElement>;

  private chatbotService = inject(ChatbotService);
  protected isChatOpen = this.chatbotService.isOpen;
  protected safeChatUrl!: SafeResourceUrl;
  private transactionStoreService = inject(TransactionStoreService);

  private readonly onIframeMessage = (event: MessageEvent<unknown>) => {
    if (event.origin !== this.chatUrl) {
      return;
    }
    if (!isRoutingMessageEvent(event)) {
      return;
    }
    const route = event.data.route;
    if (typeof route === "string" && route.startsWith("/")) {
      this.router.navigateByUrl(route);
    }
  };

  ngOnInit() {
    this.safeChatUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.chatUrl);
    window.addEventListener("message", this.onIframeMessage);
  }

  ngOnDestroy() {
    window.removeEventListener("message", this.onIframeMessage);
    // The open state lives in a root service that outlives this component, so reset it here.
    // Otherwise the panel pops back open when the component is recreated, e.g. after logout and
    // login (no page reload) or after re-enabling the Chatbot plugin.
    this.chatbotService.close();
  }

  protected closeChat() {
    this.chatbotService.close();
  }

  private sendDocumentsToIframe(type: MessageType, docs: any) {
    const iframe = this.chatbotIframe?.nativeElement;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({ type, data: docs }, this.chatUrl);
    }
  }

  protected sendRoutes() {
    this.sendDocumentsToIframe(MessageType.ROUTE, ROUTES_DOCUMENTS);
  }

  // Called when the user navigates to the transactions page to send transaction context to the chatbot; not wired up yet.
  protected sendTransactions() {
    const transactions = this.transactionStoreService.enrichTransaction.collection() ?? [];
    const data = transactions.map((tx) => ({
      pageContent: tx.description,
      metadata: {
        date: tx.postDate.toISOString(),
        amount: tx.quantity.actualQuantity.amount,
        currency: tx.quantity.actualQuantity.symbol,
      },
    }));
    this.sendDocumentsToIframe(MessageType.TRANSACTION, data);
  }
}
